import { useState, useEffect } from 'react';
import appConfig from '../../../config/appConfig';
import { api, apiRoutes } from '../../../api';

const toLatinDigits = (str) =>
    str.replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
       .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

export default function Step1_PersonalInfo({ data, onChange, onNext }) {
    const [errors, setErrors] = useState({});
    const [isAgent, setIsAgent] = useState(false);

    const token = localStorage.getItem(appConfig.auth.accessTokenKey);
    const isAuthenticated = !!token;

    // Pre-fill from API if authenticated — needed to get national_id
    useEffect(() => {
        if (!token) return;
        api.get(apiRoutes.auth.user, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            const u = res.data;
            // block if already an agent
            if (u.is_agent) { setIsAgent(true); return; }
            onChange(prev => ({
                ...prev,
                full_name: prev.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                phone:       prev.phone       || u.phone       || '',
                email:       prev.email       || u.email       || '',
                national_id: prev.national_id || u.national_id || '',
            }));
        }).catch(() => {
            // token might be expired — fall back to localStorage
            try {
                const profile = JSON.parse(localStorage.getItem('user_profile') || '{}');
                if (profile.is_agent) { setIsAgent(true); return; }
                onChange(prev => ({
                    ...prev,
                    full_name:   prev.full_name   || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
                    phone:       prev.phone       || profile.phone       || '',
                    email:       prev.email       || profile.email       || '',
                    national_id: prev.national_id || profile.national_id || '',
                }));
            } catch { /* ignore */ }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const set = (field, val) => onChange(prev => ({ ...prev, [field]: val }));

    // If already an agent, block the form
    if (isAgent) {
        return (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏅</div>
                <h2 className="aaw-step-title">شما قبلاً نماینده سایتا هستید</h2>
                <p className="aaw-step-desc" style={{ marginBottom: 24 }}>
                    حساب کاربری شما دارای دسترسی نماینده است و نیازی به ثبت درخواست مجدد ندارید.
                </p>
                <a href="/agents" className="aaw-btn aaw-btn-primary" style={{ textDecoration: 'none' }}>
                    رفتن به پنل نمایندگان ←
                </a>
            </div>
        );
    }

    const handleNationalId = (e) => {
        const val = toLatinDigits(e.target.value).replace(/\D/g, '');
        set('national_id', val);
    };

    const validate = () => {
        const e = {};
        if (!data.full_name?.trim()) e.full_name = 'نام کامل الزامی است';
        if (!data.national_id?.trim() || !/^\d{10}$/.test(data.national_id))
            e.national_id = 'کد ملی باید ۱۰ رقم عدد باشد';
        if (!isAuthenticated && !data.phone?.trim())
            e.phone = 'شماره تماس الزامی است';
        if (data.email && data.email.trim() && !/\S+@\S+\.\S+/.test(data.email))
            e.email = 'فرمت ایمیل صحیح نیست';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    return (
        <div>
            <h2 className="aaw-step-title">اطلاعات شخصی</h2>
            <p className="aaw-step-desc">
                {isAuthenticated
                    ? 'اطلاعات از حساب کاربری شما پیش‌پر شده. در صورت نیاز ویرایش کنید.'
                    : 'اطلاعات هویتی خود را وارد کنید.'}
            </p>

            {isAuthenticated && (
                <div style={{
                    padding: '10px 14px', borderRadius: 10, background: '#e8f5e9',
                    border: '1px solid #a5d6a7', color: '#2e7d32',
                    fontSize: '0.84rem', marginBottom: 20
                }}>
                    ✓ شما وارد شده‌اید. اطلاعات از پروفایل شما بارگذاری شد.
                </div>
            )}

            <div className="aaw-field">
                <label className="aaw-label">نام و نام خانوادگی *</label>
                <input
                    className={`aaw-input ${errors.full_name ? 'error' : ''}`}
                    value={data.full_name || ''}
                    onChange={e => set('full_name', e.target.value)}
                    placeholder="مثال: علی رضایی"
                />
                {errors.full_name && <span className="aaw-field-error">{errors.full_name}</span>}
            </div>

            <div className="aaw-row">
                <div className="aaw-field">
                    <label className="aaw-label">کد ملی *</label>
                    <input
                        className={`aaw-input ${errors.national_id ? 'error' : ''}`}
                        value={data.national_id || ''}
                        onChange={handleNationalId}
                        placeholder="10 رقم"
                        maxLength={10}
                        inputMode="numeric"
                    />
                    {errors.national_id && <span className="aaw-field-error">{errors.national_id}</span>}
                </div>
                <div className="aaw-field">
                    <label className="aaw-label">
                        شماره تماس {isAuthenticated ? '' : '*'}
                    </label>
                    <input
                        className={`aaw-input ${errors.phone ? 'error' : ''}`}
                        value={data.phone || ''}
                        onChange={e => set('phone', toLatinDigits(e.target.value))}
                        placeholder="09..."
                        type="tel"
                        inputMode="numeric"
                    />
                    {errors.phone && <span className="aaw-field-error">{errors.phone}</span>}
                </div>
            </div>

            <div className="aaw-field">
                <label className="aaw-label">
                    ایمیل{' '}
                    <span style={{ fontWeight: 400, color: '#90a4ae', fontSize: '0.8rem' }}>(اختیاری)</span>
                </label>
                <input
                    className={`aaw-input ${errors.email ? 'error' : ''}`}
                    value={data.email || ''}
                    onChange={e => set('email', e.target.value)}
                    placeholder="example@email.com"
                    type="email"
                />
                {errors.email && <span className="aaw-field-error">{errors.email}</span>}
            </div>

            <div className="aaw-nav">
                <div />
                <button className="aaw-btn aaw-btn-primary" onClick={() => { if (validate()) onNext(); }}>
                    مرحله بعد ←
                </button>
            </div>
        </div>
    );
}
